import { Button, Modal, TextInput } from "flowbite-react";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { WarrantyLabor } from "../../Models/WarrantyLabor";
import {
  WARRANTY_LABOR_DESCRIPTION_MAX_LENGTH,
  truncateToMaxLength,
} from "../../Constants/warrantyFieldLimits";

export default function EditWarrantyLaborModal({
  show,
  setShow,
  warrantyLabor,
  addUpdateWarrantyLabor,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  warrantyLabor: WarrantyLabor | null;
  addUpdateWarrantyLabor: (part: WarrantyLabor) => void;
}) {
  const [formData, setFormData] = useState<WarrantyLabor>({
    id: 0,
    warrantyId: 0,
    hours: 0,
    description: "",
    rate: 0,
    total: 0,
  });

  // NEW: string mirrors to make typing smooth
  const [rateText, setRateText] = useState<string>("");
  const [hoursText, setHoursText] = useState<string>("");

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  const handleSetInitialValues = () => {
    const values = warrantyLabor
      ? { ...warrantyLabor }
      : { id: 0, warrantyId: 0, hours: 0, description: "", rate: 0, total: 0 };

    setFormData(values);
    setHoursText(values.hours ? values.hours.toString() : "");
    setRateText(values.rate ? values.rate.toString() : "");
  };

  useEffect(() => {
    handleSetInitialValues();
  }, [warrantyLabor, show]);

  // Keep one function per field for clarity
  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value; // raw text
    setHoursText(v);
    const hours = v === "" ? 0 : parseFloat(v);
    setFormData((prev) => {
      const rate = prev.rate || 0;
      const total = Number.isFinite(hours * rate)
        ? +(hours * rate).toFixed(2)
        : 0;
      return { ...prev, hours: Number.isFinite(hours) ? hours : 0, total };
    });
  };

  const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value; // raw text
    setRateText(v);
    const rate = v === "" ? 0 : parseFloat(v);
    setFormData((prev) => {
      const hours = prev.hours || 0;
      const total = Number.isFinite(hours * rate)
        ? +(hours * rate).toFixed(2)
        : 0;
      return { ...prev, rate: Number.isFinite(rate) ? rate : 0, total };
    });
  };

  // Format nicely on blur (optional)
  const handleHoursBlur = () => {
    setHoursText(formData.hours ? formData.hours.toString() : "");
  };
  const handleRateBlur = () => {
    const formatted = (formData.rate ?? 0).toFixed(2);
    setRateText(formatted === "0.00" ? "" : formatted);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const next = truncateToMaxLength(
      WARRANTY_LABOR_DESCRIPTION_MAX_LENGTH,
      value
    );
    setFormData((prev) => ({ ...prev, description: next }));
  };

  const onSubmit = async () => {
    if (!formData.hours) {
      toast.error("Unable to add item. Missing Hours.");
      return;
    }
    addUpdateWarrantyLabor(formData);
  };

  function onCloseModal() {
    setShow(false);
  }

  return (
    <Modal show={show} size="4xl" onClose={onCloseModal} popup>
      <Modal.Header />
      <Modal.Body>
        <div className="w-full">
          <div className="flex justify-center mb-4">
            <h1 className="text-gray-900 font-bold text-4xl">
              {warrantyLabor === null ? "New" : "Edit"} Labor
            </h1>
          </div>
          <div className="container mx-auto p-4 bg-white rounded">
            <form>
              <div className="flex flex-col space-y-6 text-lg">
                <div>
                  <label className="font-bold mb-1 block">Description:</label>
                  <TextInput
                    id="description"
                    name="description"
                    type="text"
                    placeholder="Description..."
                    onChange={handleDescriptionChange}
                    value={formData.description ?? ""}
                    sizing="md"
                    maxLength={WARRANTY_LABOR_DESCRIPTION_MAX_LENGTH}
                  />
                </div>

                <div>
                  <label className="font-bold mb-1 block">Hours:</label>
                  <TextInput
                    id="hours"
                    name="hours"
                    type="number"
                    step="0.01"
                    placeholder="Hours..."
                    onChange={handleHoursChange}
                    onBlur={handleHoursBlur}
                    value={hoursText}
                    sizing="md"
                  />
                </div>

                <div>
                  <label className="font-bold mb-1 block">Rate:</label>
                  <TextInput
                    id="rate"
                    name="rate"
                    type="number"
                    step="0.01"
                    placeholder="Rate..."
                    onChange={handleRateChange}
                    onBlur={handleRateBlur}
                    value={rateText}
                    sizing="md"
                  />
                  <div className="text-sm text-gray-500 mt-1">
                    {formatCurrency(formData.rate ?? 0)}
                  </div>
                </div>

                <div>
                  <label className="font-bold mb-1 block">Total:</label>
                  <TextInput
                    id="total"
                    name="total"
                    type="text"
                    placeholder="Total..."
                    value={formatCurrency(formData.total)}
                    sizing="md"
                    readOnly
                  />
                </div>
              </div>

              <div className="flex justify-end pt-6">
                <Button type="button" className="buttonMain" onClick={onSubmit}>
                  Save
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
